import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, Save, Loader2, User, Building2, Mail, Phone, MapPin,
  ShoppingBag, FileText, Tag as TagIcon, X, Plus, Euro, AlertTriangle, HardHat,
} from "lucide-react";
import { toast } from "sonner";
import { fmtCents } from "./ClientsAdmin";

const PAYMENT_TERMS = [
  { v: "", label: "—" },
  { v: "immediate", label: "Paiement immédiat" },
  { v: "net_30", label: "Net 30 jours" },
  { v: "net_45", label: "Net 45 jours" },
  { v: "net_60", label: "Net 60 jours" },
  { v: "eom_30", label: "30 jours fin de mois" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function ClientDetailAdmin() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");
  const [newTag, setNewTag] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jardins-ole/admin/crm/clients/${cid}`);
      setClient(r.data);
    } catch {
      toast.error("Client introuvable");
      navigate("/admin/clients");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [cid]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...client };
      // strip computed fields server doesn't accept
      delete payload.purchase_history;
      delete payload.total_billed_cents;
      delete payload.total_paid_cents;
      delete payload.outstanding_cents;
      delete payload.orders_count;
      delete payload.worksites;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.id;
      await api.put(`/jardins-ole/admin/crm/clients/${cid}`, payload);
      toast.success("Fiche enregistrée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (!t || (client.tags || []).includes(t)) return;
    setClient({ ...client, tags: [...(client.tags || []), t] });
    setNewTag("");
  };
  const removeTag = (t) => setClient({ ...client, tags: (client.tags || []).filter((x) => x !== t) });

  if (loading || !client) {
    return (
      <div className="p-6 flex items-center justify-center text-[hsl(var(--muted-foreground))]">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  const isPro = client.type === "pro";
  const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || "—";
  const displayName = isPro && client.company_name ? client.company_name : fullName;
  const outstandingExceedsLimit = isPro && (client.online_credit_limit_cents || 0) > 0 &&
    (client.outstanding_cents || 0) > client.online_credit_limit_cents;

  return (
    <div className="p-6 space-y-5" data-testid="client-detail-admin">
      <div className="flex items-center gap-3">
        <Link to="/admin/clients">
          <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2 truncate" data-testid="client-display-name">
            {isPro ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
            {displayName}
          </h1>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            Créé le {fmtDate(client.created_at)} · Mis à jour le {fmtDate(client.updated_at)}
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="client-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {outstandingExceedsLimit && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm flex items-center gap-2" data-testid="client-credit-alert">
          <AlertTriangle className="w-4 h-4" />
          Encours <strong>{fmtCents(client.outstanding_cents)}</strong> supérieur à la limite autorisée <strong>{fmtCents(client.online_credit_limit_cents)}</strong>.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Commandes</div>
          <div className="text-2xl font-bold mt-1">{client.orders_count || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Total facturé</div>
          <div className="text-2xl font-bold mt-1">{fmtCents(client.total_billed_cents)}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Total payé</div>
          <div className="text-2xl font-bold mt-1 text-emerald-700">{fmtCents(client.total_paid_cents)}</div>
        </Card>
        <Card className={`p-3 ${(client.outstanding_cents || 0) > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Encours</div>
          <div className="text-2xl font-bold mt-1 text-amber-700" data-testid="client-outstanding">
            {fmtCents(client.outstanding_cents)}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[hsl(var(--border))]">
        {[
          { k: "info", l: "Coordonnées" },
          ...(isPro ? [{ k: "pro", l: "Infos pro" }] : []),
          { k: "history", l: `Historique (${client.orders_count || 0})` },
          { k: "worksites", l: `Chantiers (${(client.worksites || []).length})` },
          { k: "notes", l: "Notes & Tags" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.k ? "border-[color:var(--brand-sky)] text-[color:var(--brand-navy)]" : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
            data-testid={`client-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <Card className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <select
                value={client.type}
                onChange={(e) => setClient({ ...client, type: e.target.value })}
                className="w-full border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white mt-1"
                data-testid="client-type-select"
              >
                <option value="particulier">Particulier</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div>
              <Label>Type d'intervention récurrente</Label>
              <Input value={client.intervention_type || ""} onChange={(e) => setClient({ ...client, intervention_type: e.target.value })} placeholder="ex: Entretien jardin trimestriel" />
            </div>
            <div>
              <Label>Prénom</Label>
              <Input value={client.first_name || ""} onChange={(e) => setClient({ ...client, first_name: e.target.value })} />
            </div>
            <div>
              <Label>Nom</Label>
              <Input value={client.last_name || ""} onChange={(e) => setClient({ ...client, last_name: e.target.value })} />
            </div>
            <div>
              <Label><Mail className="w-3 h-3 inline mr-1" />Email</Label>
              <Input type="email" value={client.email || ""} onChange={(e) => setClient({ ...client, email: e.target.value })} />
            </div>
            <div>
              <Label><Phone className="w-3 h-3 inline mr-1" />Téléphone</Label>
              <Input value={client.phone || ""} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label><MapPin className="w-3 h-3 inline mr-1" />Adresse</Label>
              <Input value={client.address_street || ""} onChange={(e) => setClient({ ...client, address_street: e.target.value })} placeholder="Rue, n°, complément" />
            </div>
            <div>
              <Label>Code postal</Label>
              <Input value={client.address_postal_code || ""} onChange={(e) => setClient({ ...client, address_postal_code: e.target.value })} />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={client.address_city || ""} onChange={(e) => setClient({ ...client, address_city: e.target.value })} />
            </div>
          </div>
        </Card>
      )}

      {tab === "pro" && isPro && (
        <Card className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Raison sociale</Label>
              <Input value={client.company_name || ""} onChange={(e) => setClient({ ...client, company_name: e.target.value })} data-testid="client-company-name" />
            </div>
            <div>
              <Label>SIRET</Label>
              <Input value={client.siret || ""} onChange={(e) => setClient({ ...client, siret: e.target.value })} />
            </div>
            <div>
              <Label>N° TVA intracom</Label>
              <Input value={client.vat_number || ""} onChange={(e) => setClient({ ...client, vat_number: e.target.value })} />
            </div>
            <div>
              <Label>Conditions de paiement</Label>
              <select
                value={client.payment_terms || ""}
                onChange={(e) => setClient({ ...client, payment_terms: e.target.value })}
                className="w-full border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white mt-1"
                data-testid="client-payment-terms"
              >
                {PAYMENT_TERMS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label><Euro className="w-3 h-3 inline mr-1" />Encours max boutique en ligne (€)</Label>
              <Input
                type="number" min={0} step={10}
                value={(client.online_credit_limit_cents || 0) / 100}
                onChange={(e) => setClient({ ...client, online_credit_limit_cents: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                placeholder="0 = pas de limite"
                data-testid="client-credit-limit"
              />
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Bloquera le passage en caisse en ligne au-delà de cette limite (encours non payé).
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === "history" && (
        <Card className="p-5">
          {(client.purchase_history || []).length === 0 ? (
            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
              <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
              Aucune commande pour ce client pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] border-b">
                  <tr>
                    <th className="px-3 py-2 text-left">N°</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Articles</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Paiement</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {(client.purchase_history || []).map((o) => (
                    <tr key={o.id} className="border-b hover:bg-[hsl(var(--muted))]/20" data-testid={`order-row-${o.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{o.number || o.id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 text-xs">{fmtDate(o.created_at)}</td>
                      <td className="px-3 py-2 text-xs">{o.items_count} article{o.items_count > 1 ? "s" : ""}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-[hsl(var(--muted))]">{o.status || "—"}</span></td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-[hsl(var(--muted))]">{o.payment_status || "—"}</span></td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtCents(o.total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "worksites" && (
        <Card className="p-5">
          {(client.worksites || []).length === 0 ? (
            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
              <HardHat className="w-10 h-10 mx-auto mb-2 opacity-30" />
              Aucun chantier enregistré.
              <div className="mt-4">
                <Link to={`/admin/chantiers/new?client_id=${client.id}`}>
                  <Button data-testid="client-add-worksite-btn"><Plus className="w-4 h-4 mr-2" />Ajouter un chantier</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-end mb-2">
                <Link to={`/admin/chantiers/new?client_id=${client.id}`}>
                  <Button size="sm"><Plus className="w-4 h-4 mr-2" />Nouveau chantier</Button>
                </Link>
              </div>
              {(client.worksites || []).map((w) => (
                <Link key={w.id} to={`/admin/chantiers/${w.id}`} className="block">
                  <Card className="p-3 hover:border-[color:var(--brand-sky)] cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{w.title}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                          {w.location_city || "—"} · {w.duration_days || 0} j · {(w.after_photos || []).length} photos après
                        </div>
                      </div>
                      <div>
                        {w.is_published ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Publié</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">Brouillon</span>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "notes" && (
        <Card className="p-5 space-y-4">
          <div>
            <Label><TagIcon className="w-3 h-3 inline mr-1" />Tags</Label>
            <div className="flex flex-wrap gap-1 mt-1 mb-2">
              {(client.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-[hsl(var(--muted))] px-2 py-1 rounded text-xs" data-testid={`tag-${t}`}>
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Ajouter un tag (ex: Client fidèle, Devis en cours...)"
                data-testid="new-tag-input"
              />
              <Button type="button" variant="outline" onClick={addTag}><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <Label><FileText className="w-3 h-3 inline mr-1" />Notes &amp; mémo interne</Label>
            <Textarea
              rows={10}
              value={client.notes_internal || ""}
              onChange={(e) => setClient({ ...client, notes_internal: e.target.value })}
              placeholder="Notes internes visibles uniquement par l'équipe..."
              data-testid="client-notes-textarea"
            />
          </div>
        </Card>
      )}
    </div>
  );
}
